import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  isActiveContract,
  getIsShowExitChecklist,
} from "@rentiohq/shared-frontend/dist/utils/contract.utils";
import {
  memberHasOneOfRoles,
  memberHasRole,
} from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  Card,
  RentioInternalRenderer,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import React from "react";
import { ConfirmLegalActivationModal } from "scenes/RentioLegal/components/ConfirmLegalActivationModal";
import { useIsRentioLegalActivated } from "scenes/RentioLegal/hooks/useIsRentioLegalActivated";
import { NumberParam, StringParam } from "serialize-query-params";
import { IndexContractModal } from "../../../../../../components/IndexContractModalV2";
import { ContractMetaData } from "./components/ContractMetaData";
import { EntryAdministration } from "./components/EntryAdministration";
import { ExitAdministration } from "./components/ExitAdministration";
import { IndexationCard } from "./components/IndexationCard";
import { LinkedProperties } from "./components/LinkedProperties";
import { RentioLegalCard } from "./components/RentioLegalCard";
import { getIndexationMessage } from "./ContractCard.utils";

interface IProps {
  contract: IContract;
  property: IProperty;
  showContractType?: boolean;
  showChecklistInitialState?: boolean;
  onCancelContract?: () => void;
}

export const ContractCard = (props: IProps) => {
  const {
    contract,
    property,
    showContractType = true,
    showChecklistInitialState = true,
    onCancelContract,
  } = props;
  const { internalModeEnabled } = useInternalMode();

  const contractIsActive = isActiveContract(contract);
  const showExitChecklist = getIsShowExitChecklist(contract);

  const hasRentioLegal = useIsRentioLegalActivated();

  const [showEntryChecklistItems, setShowEntryChecklistItems] =
    React.useState(false);
  const [showExitChecklistItems, setShowExitChecklistItems] =
    React.useState(false);

  const [_, setQueryParamValue] = useQueryParams({
    documentId: StringParam,
    rentDepositId: NumberParam,
  });

  // Local state
  const [showIndexContractModal, setShowIndexContractModal] =
    React.useState(false);
  const [contractToEnableRentioLegalFor, setContractToEnableRentioLegalFor] =
    React.useState<IContract>();

  // Redux selectors
  const { isBroker, broker } = authHooks.useSelf();

  const hasPropertyManagement = property.members.some(member => {
    const isLoggedInBroker =
      isBroker && member.account.id === broker?.accountId;
    const hasMemberRole = memberHasRole(member, EPropertyMemberTypes.Broker);
    const hasManagerRole = memberHasOneOfRoles(member, [
      EPropertyMemberTypes.IndexationManager,
      EPropertyMemberTypes.FinancialManager,
      EPropertyMemberTypes.TechnicalManager,
    ]);

    return isLoggedInBroker && hasMemberRole && hasManagerRole;
  });

  React.useEffect(() => {
    if (
      contractIsActive &&
      hasPropertyManagement &&
      showChecklistInitialState &&
      !showExitChecklist
    ) {
      setShowEntryChecklistItems(false);
    }
  }, [contractIsActive]);

  React.useEffect(() => {
    if (showExitChecklist && showChecklistInitialState) {
      setShowEntryChecklistItems(false);
      setShowExitChecklistItems(true);
    }
  }, [showExitChecklist]);

  React.useEffect(() => {
    if (!showExitChecklist && showChecklistInitialState) {
      setShowEntryChecklistItems(true);
    }
  }, [showExitChecklist]);

  // Event handlers
  const handleIndexModalClose = (documentId?: string) => {
    setShowIndexContractModal(false);
    setQueryParamValue({
      documentId,
    });
  };

  const handleActivateRentioLegal = async (contract: IContract) => {
    if (!hasRentioLegal) {
      return;
    }

    setContractToEnableRentioLegalFor(contract);
  };

  const { ibanisationStatus, linkedProperties = [] } = contract;
  const indexationItem = getIndexationMessage({
    property,
    contract,

    // Callbacks
    onIndexContract: () => setShowIndexContractModal(true),

    // Config
    internalModeEnabled,
  });

  // Render
  return (
    <>
      <Card key={contract.id} hasSections={true}>
        <Card.Section>
          <ContractMetaData
            contract={contract}
            property={property}
            showContractType={showContractType}
            onCancelContract={onCancelContract}
          />
        </Card.Section>

        {linkedProperties.length > 0 && (
          <Card.Section>
            <LinkedProperties contract={contract} />
          </Card.Section>
        )}

        <RentioInternalRenderer
          items={{
            ibanisationStatus,
          }}
        />
        {indexationItem && (
          <IndexationCard indexationItem={indexationItem} contract={contract} />
        )}

        <EntryAdministration
          contract={contract}
          property={property}
          showCircles={showChecklistInitialState && !showExitChecklist}
          showChecklistItems={showEntryChecklistItems}
          setShowChecklistItems={setShowEntryChecklistItems}
        />

        {showExitChecklist && (
          <ExitAdministration
            contract={contract}
            property={property}
            showCircles={showChecklistInitialState}
            showChecklistItems={showExitChecklistItems}
            setShowChecklistItems={setShowExitChecklistItems}
          />
        )}

        {showIndexContractModal && (
          <IndexContractModal
            contractId={contract.id}
            onClose={handleIndexModalClose}
          />
        )}

        <RentioLegalCard
          contract={contract}
          onActivateRentioLegal={handleActivateRentioLegal}
        />
      </Card>

      {contractToEnableRentioLegalFor && (
        <ConfirmLegalActivationModal
          contract={contractToEnableRentioLegalFor}
          onSuccess={() => setContractToEnableRentioLegalFor(undefined)}
          onClose={() => setContractToEnableRentioLegalFor(undefined)}
        />
      )}
    </>
  );
};
